import * as React from 'react';
import { Component } from 'react';
import { computed, isObservableArray, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import get from 'lodash/get';
import find from 'lodash/find';
import formatter from '../utils/formatter';
import { performanceCheck as checkPerformance } from '../utils/performanceCheck';
import { getDisclaimers } from '../utils/disclaimers';

// Higher-order component to return a FundCard with data and methods required to function
const withShareclass = WrappedComponent => {
    @inject('dataStore')
    @observer
    class FundCard extends Component<any, any> {
        @computed
        get noDataHidden() {
            return this.props.fundFacts.noDataHidden;
        }

        @computed
        get noDataPlaceholder() {
            return (
                this.props.fundFacts.noDataPlaceholder ||
                this.props.dataStore.config.noDataPlaceholder
            );
        }

        @computed
        get hasPerformance() {
            const { dataStore, shareClass } = this.props;
            const { config } = dataStore;
            return checkPerformance({
                dataStore,
                config,
                shareClass,
            });
        }

        @computed
        get fundFacts() {
            return this.getColumnsWithData(this.props.fundFacts.cards);
        }

        @computed
        get cards() {
            return this.fundFacts.filter(f => f.primary);
        }

        @computed
        get rows() {
            return this.fundFacts.filter(f => !f.primary);
        }

        getColumnsWithData = (columns = []) => {
            let footnoteIndex = -1;

            const { dataStore } = this.props;
            const values = columns.map((column, index) => {
                const {
                    property,
                    data,
                    disclaimer,
                    asAtDate,
                    noDataHidden,
                    noDataPlaceholder,
                    performanceCheck,
                } = column;

                const columnHasFootnote = disclaimer && disclaimer.footnote;

                if (isObservableArray(data)) {
                    const dataValues = data.map(d => ({
                        ...d,
                        value: this.getDataPoint(d),
                    }));
                    const dataHasValues = !dataValues.some(
                        d => d.value === false
                    );
                    if (dataHasValues && columnHasFootnote) {
                        footnoteIndex++;
                    }

                    return {
                        ...column,
                        multiple: true,
                        data: dataValues,
                        asAtDate: {
                            ...asAtDate,
                            value: asAtDate
                                ? this.getDataPoint(asAtDate.data)
                                : undefined,
                        },
                        disclaimers: dataHasValues
                            ? this.getDisclaimerContent(
                                  footnoteIndex,
                                  disclaimer
                              )
                            : {},
                    };
                }

                if (data) {
                    const value = this.getDataPoint({ dataStore, ...data });
                    if (value && columnHasFootnote) {
                        footnoteIndex++;
                    }

                    return {
                        ...column,
                        data: {
                            ...data,
                            value,
                        },
                        asAtDate: {
                            ...asAtDate,
                            value: asAtDate
                                ? this.getDataPoint(asAtDate.data)
                                : undefined,
                        },
                        disclaimers: value
                            ? this.getDisclaimerContent(
                                  footnoteIndex,
                                  disclaimer
                              )
                            : {},
                    };
                }

                // Support simple label/property column definition
                if (property) {
                    const value = this.getDataPoint({
                        property: 'properties_pub',
                        code: property,
                        key: 'value',
                        noDataHidden,
                        noDataPlaceholder,
                        performanceCheck,
                    });
                    if (value && columnHasFootnote) {
                        footnoteIndex++;
                    }

                    return {
                        ...column,
                        data: {
                            ...data,
                            value,
                        },
                        asAtDate: {
                            ...asAtDate,
                            value: asAtDate
                                ? this.getDataPoint(asAtDate.data)
                                : undefined,
                        },
                        disclaimers: value
                            ? this.getDisclaimerContent(
                                  footnoteIndex,
                                  disclaimer
                              )
                            : {},
                    };
                }

                return {
                    ...column,
                    data: { value: this.noDataPlaceholder },
                };
            });

            return values;
        };

        getDataPoint = dataConfig => {
            const {
                property,
                code,
                ccy,
                classification,
                periodicity,
                find: findObj,
                key,
                index,
                format,
                noDataHidden = this.noDataHidden,
                noDataPlaceholder = this.noDataPlaceholder,
                performanceCheck,
                dataStore,
            } = dataConfig;

            if (performanceCheck && !this.hasPerformance) {
                return noDataHidden ? false : noDataPlaceholder;
            }

            // valuePlaceholder is the default value when there is no data point found.
            // It will either be false or a noDataPlaceholder character.
            // When the value is false we will not render the item.
            // If noDataHidden hidden is configured for all items or individually we return false.
            // Otherwise we use the noDataPlaceholder configured individually or for all.
            const valuePlaceholder = noDataHidden ? false : noDataPlaceholder;

            if (property === 'properties_pub') {
                const value = get(
                    this.props.shareClass,
                    `${property}.${code}.${key}`,
                    valuePlaceholder
                );
                return format
                    ? formatter({
                          dataStore,
                          value,
                          type: format.type,
                          locale: this.locale,
                          currency: this.fundCurrency,
                          options: format.options,
                      })
                    : value;
            }

            if (property) {
                const dataSet = get(this.props.shareClass, property, []);

                // We should end up with one data object, so get the first item in array
                const data = find(dataSet, {
                    ...(code && { code }),
                    ...(ccy && { ccy }),
                    ...(classification && { classification }),
                    ...(periodicity && { periodicity }),
                });

                // Get the values from data
                const { values = [] } = data || {};

                // If we have a find object and key, find and extract a value
                if (findObj && key) {
                    const valueObj = find(values, findObj);
                    const value = get(valueObj, key, valuePlaceholder);
                    return format
                        ? formatter({
                              dataStore,
                              value,
                              type: format.type,
                              locale: this.locale,
                              currency: this.fundCurrency,
                              options: format.options,
                          })
                        : value;
                }

                // If we have a positive index and key, get index and extract a value
                if (index >= 0 && key) {
                    const value = get(
                        values,
                        `[${index}].${key}`,
                        valuePlaceholder
                    );
                    return format
                        ? formatter({
                              dataStore,
                              value,
                              type: format.type,
                              locale: this.locale,
                              currency: this.fundCurrency,
                              options: format.options,
                          })
                        : value;
                }

                // If we have a negative index and key, get last item and extract a value
                if (index < 0 && key) {
                    const value = get(
                        values,
                        `[${values.length - 1}].${key}`,
                        valuePlaceholder
                    );
                    return format
                        ? formatter({
                              dataStore,
                              value,
                              type: format.type,
                              locale: this.locale,
                              currency: this.fundCurrency,
                              options: format.options,
                          })
                        : value;
                }
            }

            return valuePlaceholder;
        };

        getDisclaimerContent = (index, disclaimer = {} as any) => {
            const { footnote } = disclaimer;
            let character;
            const content = this.disclaimerByType([disclaimer])[0];

            if (footnote && this.footnoteScheme === 'symbol') {
                const accessor = index % this.footnoteCharacters.length;
                character = get(this.footnoteCharacters, accessor, '*');
            }

            if (footnote && this.footnoteScheme === 'numeric') {
                character = index + 1;
            }

            return {
                character,
                content,
                footnote,
            };
        };

        @computed
        get fundCurrency() {
            return get(
                this.props.shareClass,
                `properties_pub.${this.props.dataStore.config
                    .fundCurrencyProperty || 'ccy'}.value`
            );
        }

        @computed
        get locale() {
            return this.props.dataStore.culture;
        }

        @computed
        get footnoteScheme() {
            const {
                footnoteScheme = 'numeric',
            } = this.props.dataStore.config.rowConfig;
            return footnoteScheme;
        }

        @computed
        get footnoteCharacters() {
            const {
                footnoteCharacters = [],
            } = this.props.dataStore.config.rowConfig;
            return footnoteCharacters;
        }

        @computed
        get cardDisclaimers() {
            return this.fundFacts
                .filter(card => card.disclaimers.footnote)
                .map(card => {
                    return card.disclaimers;
                });
        }

        @computed
        get disclaimers() {
            return this.disclaimerByType(this.props.fundFacts.disclaimers);
        }

        @action
        disclaimerByType(disclaimerTypes) {
            const { disclaimers = {} } = this.props.shareClass;
            return getDisclaimers(
                this.props.dataStore,
                disclaimerTypes,
                disclaimers
            );
        }

        render() {
            // TODO: Is there a DRYer way to get these as props on to the WrappedComponent?
            // Spreading {...this} doesn't work
            const { cards, rows, disclaimers, cardDisclaimers } = this;

            return (
                <WrappedComponent
                    {...this.props}
                    {...{
                        cards,
                        rows,
                        disclaimers,
                        cardDisclaimers,
                    }}
                />
            );
        }
    }

    return FundCard;
};

export default withShareclass;



// WEBPACK FOOTER //
// ./src/Components/withColumns.tsx